import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Users, 
  TrendingUp, 
  Save, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Package,
  Settings,
  BarChart3,
  Database,
  HardDrive,
  FileText,
  UserPlus
} from 'lucide-react';
import { companyDatabase } from '../utils/mainAppDatabase';
import { loadSubscriptionPlans } from '../utils/subscriptionUtils';

const EMPTY_LIMITS = {
  firebase_operations: 0,
  storage_mb: 0,
  sales_documents: 0,
  parties: 0
};


const SubscriptionManager = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [customLimits, setCustomLimits] = useState({ ...EMPTY_LIMITS });
  const [entitlementMode, setEntitlementMode] = useState(false); // false = use plan limits, true = custom limits
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [companyTopups, setCompanyTopups] = useState([]);
  const [totalEntitlements, setTotalEntitlements] = useState(null);
  const [subscriptionVersion, setSubscriptionVersion] = useState(0);
  const [plans, setPlans] = useState({}); // canonical catalog only (no static fallback)
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [lastSubscriptionSync, setLastSubscriptionSync] = useState(null);
  const [lastUsageSync, setLastUsageSync] = useState(null);
  const [lastCompaniesSync, setLastCompaniesSync] = useState(null);
  const [isRefreshingOperational, setIsRefreshingOperational] = useState(false);

  const resolveCompanyPlan = (company) =>
    company?.subscription_details?.subscription?.basePlan?.planId ||
    company?.subscription_details?.subscription?.planId ||
    company?.subscription_details?.subscription?.plan ||
    company?.subscription?.basePlan?.planId ||
    company?.subscription?.planId ||
    company?.subscription?.plan ||
    company?.plan ||
    'starter';

  // Load plans from Firebase on mount
  useEffect(() => {
    loadPlans();
  }, []);

  // Load companies
  useEffect(() => {
    loadCompanies();
  }, []);

  // Load company subscription when selected
  useEffect(() => {
    if (selectedCompany) {
      loadCompanySubscription(selectedCompany.id);
      loadUsageData(selectedCompany.id);
      loadCompanyTopups(selectedCompany.id);
    }
  }, [selectedCompany]);

  // Load plans from Firebase (canonical catalog — no silent static fallback)
  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      setPlansError('');
      const firebasePlans = await loadSubscriptionPlans();

      if (firebasePlans && Object.keys(firebasePlans).length > 0) {
        setPlans(firebasePlans);
        setPlansError('');
      } else {
        setPlans({});
        setPlansError('No plans in Plan Catalog. Add plans before changing subscriptions.');
      }
    } catch (error) {
      console.error('Error loading plans from Firebase:', error);
      setPlans({});
      setPlansError(error.message || 'Failed to load plan catalog');
    } finally {
      setPlansLoading(false);
    }
  };

  const loadCompanyTopups = async (companyId) => {
    try {
      const { getAppFirestore } = await import('../appFirebaseClient');
      const { doc, getDoc } = await import('firebase/firestore');
      const appDb = getAppFirestore();
      
      const subscriptionRef = doc(appDb, 'companies', companyId, 'subscription_details', 'subscription');
      const subscriptionSnap = await getDoc(subscriptionRef);
      
      if (subscriptionSnap.exists()) {
        const subscription = subscriptionSnap.data();
        const topups = subscription.topups || [];
        const totalEnt = subscription.totalEntitlements || null;
        
        setCompanyTopups(topups);
        setTotalEntitlements(totalEnt);
        
        console.log('✅ Loaded topups:', topups.length);
      } else {
        setCompanyTopups([]);
        setTotalEntitlements(null);
      }
    } catch (error) {
      console.error('❌ Error loading topups:', error);
      setCompanyTopups([]);
      setTotalEntitlements(null);
    }
  };

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const companiesData = await companyDatabase.getAllCompanies();
      setCompanies(companiesData);
      setLastCompaniesSync(new Date().toISOString());
    } catch (error) {
      setMessage({ type: 'error', text: `Error loading companies: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const loadCompanySubscription = async (companyId) => {
    try {
      console.log('🔄 Loading normalized subscription via adapter for:', companyId);
      const { readSubscription } = await import('../utils/subscriptionUtils');
      const normalized = await readSubscription(companyId);

      const defaultLimits = plans['starter']?.limits || EMPTY_LIMITS;
      const nextPlanId = normalized?.planId || normalized?.plan || 'starter';
      const canonicalLimits = normalized?.basePlan?.limits || normalized?.limits || defaultLimits;

      setSelectedPlan(nextPlanId);
      setTotalEntitlements(normalized?.totalEntitlements || normalized?.limits || null);
      setSubscriptionVersion(Number(normalized?.version || 0));

      const hasCustom =
        normalized?.hasCustomEntitlement ||
        normalized?.basePlan?.hasCustomEntitlement ||
        Boolean(normalized?.customLimits);
      if (hasCustom) {
        setEntitlementMode(true);
        setCustomLimits({
          ...defaultLimits,
          ...(normalized?.customLimits || normalized?.basePlan?.customLimits || canonicalLimits)
        });
      } else {
        setEntitlementMode(false);
        setCustomLimits(plans[nextPlanId]?.limits || defaultLimits);
      }

      console.log('✅ Normalized subscription loaded:', {
        planId: nextPlanId,
        version: normalized?.version || 0
      });
      setLastSubscriptionSync(new Date().toISOString());
    } catch (error) {
      console.error('❌ Error loading subscription:', error);
      // Set default values on error
      const defaultLimits = plans['starter']?.limits || EMPTY_LIMITS;
      setSelectedPlan('starter');
      setEntitlementMode(false);
      setCustomLimits(defaultLimits);
      setSubscriptionVersion(0);
    }
  };

  const loadUsageData = async (companyId) => {
    try {
      console.log('🔄 Loading real-time usage data from acctoo-app tracking system for:', companyId);
      
      // Import backoffice auth for token
      const { backofficeAuth } = await import('../backofficeFirebaseClient');
      
      const token = await backofficeAuth.currentUser?.getIdToken?.();
      if (!token) {
        throw new Error('Admin authentication required');
      }

      // Use API endpoint to get company details first
      const response = await fetch(`https://us-central1-acctoo-backoffice.cloudfunctions.net/api/v1/companies/${companyId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      const company = data?.company || data;
      
      // Now fetch REAL-TIME usage data from acctoo-app tracking system
      // This ensures we get the actual counts from the client-side tracking
      const { ensureAppServiceSignIn } = await import('../appFirebaseClient');
      await ensureAppServiceSignIn();
      
      const { getAppFirestore } = await import('../appFirebaseClient');
      const { doc, getDoc, collection, getDocs } = await import('firebase/firestore');
      
      const db = getAppFirestore();
      
      let operations = 0;
      let storage = 0;
      
      // Get current month (YYYY-MM) for usage tracking
      const currentMonth = new Date().toISOString().slice(0, 7);
      
      try {
        // PRIMARY SOURCE: Get usage from subscription.currentMonthUsage (same as app)
        // This ensures backoffice and app read from the same data source
        const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
        const subscriptionSnap = await getDoc(subscriptionRef);
        
        if (subscriptionSnap.exists()) {
          const subscriptionData = subscriptionSnap.data();
          const currentMonthUsage = subscriptionData.currentMonthUsage || {};
          
          // Use same field names as app: firebase_operations, storage_mb
          operations = Math.round(currentMonthUsage.firebase_operations || currentMonthUsage.operations || 0);
          storage = Math.round((currentMonthUsage.storage_mb || currentMonthUsage.storage || 0) * 100) / 100;
          
          console.log('✅ Usage data from subscription.currentMonthUsage (same as app):', { 
            operations, 
            storage
          });
        }
        
        // FALLBACK: If currentMonthUsage doesn't have data, fetch from collections
        // This handles edge cases where currentMonthUsage might be empty
        if (operations === 0 && storage === 0) {
          console.log('📊 Fallback: Fetching from usage collection');
          
          // Try usage collection for operations/storage
          try {
            const usageRef = doc(db, 'companies', companyId, 'usage', currentMonth);
            const usageSnap = await getDoc(usageRef);
            if (usageSnap.exists()) {
              const usageData = usageSnap.data();
              operations = Math.round(usageData.operations || 0);
              storage = Math.round((usageData.storage || 0) * 100) / 100;
              console.log('✅ Fallback: Usage data from collection:', { operations, storage });
            }
          } catch (e) {
            console.log('ℹ️ Usage collection not available');
          }
        }
      } catch (usageError) {
        console.error('❌ Error fetching usage data:', usageError);
        // Continue with 0 values if all methods fail
      }
      
      console.log('✅ Real-time usage data loaded:', { 
        operations, 
        storage
      });
      
      setUsageData({
        operations,
        storage
      });
      setLastUsageSync(new Date().toISOString());
    } catch (error) {
      console.error('❌ Error loading real-time usage:', error);
      // Set default values on error
      setUsageData({
        operations: 0,
        storage: 0
      });
    }
  };

  const refreshOperationalData = async () => {
    setIsRefreshingOperational(true);
    try {
      await loadCompanies();
      await loadPlans();
      if (selectedCompany) {
        await loadCompanySubscription(selectedCompany.id);
        await loadUsageData(selectedCompany.id);
        await loadCompanyTopups(selectedCompany.id);
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Refresh failed' });
    } finally {
      setIsRefreshingOperational(false);
    }
  };

  const handlePlanChange = (planId) => {
    setSelectedPlan(planId);
    if (!entitlementMode) {
      const planLimits = plans[planId]?.limits || {};
      setCustomLimits(planLimits);
    }
  };

  const handleLimitChange = (key, value) => {
    setCustomLimits(prev => ({
        ...prev,
      [key]: parseInt(value) || 0
    }));
  };

  const handleSaveSubscription = async () => {
    if (!selectedCompany) {
      setMessage({ type: 'error', text: 'Please select a company first' });
      return;
    }

    if (plansError || Object.keys(plans).length === 0) {
      setMessage({ type: 'error', text: 'Plan catalog is empty or failed to load. Open Plan Catalog, then refresh operational data.' });
      return;
    }

    try {
      
      // Import canonical write adapter
      const { writeSubscription } = await import('../utils/subscriptionUtils');
      
      // Import backoffice auth for admin info
      const { backofficeAuth } = await import('../backofficeFirebaseClient');
      const currentUser = backofficeAuth.currentUser;
      
      if (!currentUser) {
        throw new Error('Admin authentication required');
      }

      // Canonical catalog only — no static pricing fallback
      const currentPlan = plans[selectedPlan];
      if (!currentPlan) {
        throw new Error(`Plan "${selectedPlan}" is not in the Plan Catalog. Refresh catalog or pick a valid plan.`);
      }

      // Verify plan has billing field
      if (!currentPlan.billing) {
        console.warn(`⚠️ Plan ${selectedPlan} missing billing field, defaulting to monthly`);
      }

      // Prepare subscription data
      const subscriptionData = {
        planId: selectedPlan,
        planName: currentPlan.name,
        price: currentPlan.price,
        currency: currentPlan.currency || '₹',
        billing: currentPlan.billing || 'monthly', // Include billing period for date calculation
        limits: entitlementMode ? customLimits : currentPlan.limits,
        customLimits: entitlementMode ? customLimits : null,
        hasCustomEntitlement: entitlementMode,
        status: 'active'
      };

      // Debug: Log billing period being sent
      console.log('📋 Subscription update data:', {
        planId: selectedPlan,
        planName: currentPlan.name,
        billing: subscriptionData.billing,
        price: subscriptionData.price,
        limits: subscriptionData.limits,
        planData: currentPlan // Log full plan to verify structure
      });

      // Admin info for logging
      const adminInfo = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || currentUser.email
      };

      const result = await writeSubscription(
        selectedCompany.id,
        subscriptionData,
        adminInfo,
        { expectedVersion: subscriptionVersion }
      );
      
      setMessage({ 
        type: 'success', 
        text: `Subscription updated successfully for ${selectedCompany.name}!` 
      });
      
      console.log('✅ Subscription saved successfully:', result);
      
      // Small delay to ensure database consistency
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reload data
      await loadCompanySubscription(selectedCompany.id);
      await loadUsageData(selectedCompany.id);
      
    } catch (error) {
      console.error('❌ Error saving subscription:', error);
      const msg = error.message || '';
      if (String(msg).includes('409')) {
        setMessage({
          type: 'error',
          text: 'Concurrent update (version conflict). Use Refresh operational data, then retry.'
        });
      } else {
        setMessage({ type: 'error', text: `Error saving: ${msg}` });
      }
    } finally {
      setLoading(false);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.round((used / limit) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getCurrentLimits = () => {
    // If totalEntitlements exist (new structure), use them
    if (totalEntitlements) {
      return {
        firebase_operations: totalEntitlements.firebase_operations || 0,
        storage_mb: totalEntitlements.storage_mb || 0
      };
    }
    // Otherwise use old structure
    const planLimits = plans[selectedPlan]?.limits || {};
    return entitlementMode ? customLimits : planLimits;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Subscription Manager</h1>
          </div>
          <p className="text-gray-600">Manage company subscriptions and usage limits</p>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-white border rounded-lg p-4">
            <div className="text-xs text-gray-600 space-y-1">
              <div>
                <span className="font-semibold text-gray-800">Companies list synced:</span>{' '}
                {lastCompaniesSync || '—'}
              </div>
              <div>
                <span className="font-semibold text-gray-800">Subscription (selected) synced:</span>{' '}
                {lastSubscriptionSync || '—'}
              </div>
              <div>
                <span className="font-semibold text-gray-800">Usage snapshot synced:</span>{' '}
                {lastUsageSync || '—'}
              </div>
              {plansError && <div className="text-red-700 font-medium">Plan catalog: {plansError}</div>}
              {plansLoading && <div className="text-gray-500">Loading plan catalog…</div>}
            </div>
            <button
              type="button"
              onClick={refreshOperationalData}
              disabled={isRefreshingOperational || loading}
              className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshingOperational ? 'animate-spin' : ''}`} />
              Refresh operational data
            </button>
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? 
              <CheckCircle className="w-5 h-5" /> : 
              <AlertCircle className="w-5 h-5" />
            }
            <span>{message.text}</span>
              </div>
            )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Company Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-800">Select Company</h2>
              </div>
              
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {companies.map(company => (
                  <div
                    key={company.id}
                    onClick={() => setSelectedCompany(company)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCompany?.id === company.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-800">{company.name}</div>
                    <div className="text-sm text-gray-600">{company.id}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        resolveCompanyPlan(company) === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                        resolveCompanyPlan(company) === 'pro' ? 'bg-blue-100 text-blue-700' :
                        resolveCompanyPlan(company) === 'basic' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {resolveCompanyPlan(company).toUpperCase()}
                      </span>
                    </div>
                </div>
                ))}
            </div>
          </div>
      </div>

          {/* Right Panel - Subscription Details */}
          <div className="lg:col-span-2 space-y-6">
            {selectedCompany ? (
              <>
                {/* Selected Company Info */}
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold mb-2">{selectedCompany.name}</h2>
                      <p className="text-blue-100">Company ID: {selectedCompany.id}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-blue-100 mb-1">Current Plan</div>
                      <div className={`px-4 py-2 rounded-lg font-bold text-lg ${
                        resolveCompanyPlan(selectedCompany) === 'enterprise' ? 'bg-purple-500' :
                        resolveCompanyPlan(selectedCompany) === 'pro' ? 'bg-blue-400' :
                        resolveCompanyPlan(selectedCompany) === 'basic' ? 'bg-green-500' :
                        'bg-gray-400'
                      }`}>
                        {resolveCompanyPlan(selectedCompany).toUpperCase()}
                      </div>
                    </div>
                  </div>
          </div>
          
                {/* Total Entitlements (if new structure) */}
                {totalEntitlements && (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-6 border-2 border-purple-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Package className="w-5 h-5 text-purple-600" />
                      <h3 className="text-xl font-bold text-gray-800">Total Entitlements (Base + Top-ups)</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Total Operations</div>
                        <div className="text-2xl font-bold text-purple-700">
                          {totalEntitlements.firebase_operations?.toLocaleString() || 0}
                        </div>
                        {totalEntitlements.breakdown && (
                          <div className="text-xs text-gray-500 mt-1">
                            Base: {totalEntitlements.breakdown.fromBasePlan?.firebase_operations?.toLocaleString() || 0} | 
                            Top-ups: {totalEntitlements.breakdown.fromTopups?.firebase_operations?.toLocaleString() || 0}
                          </div>
                        )}
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Total Storage</div>
                        <div className="text-2xl font-bold text-purple-700">
                          {totalEntitlements.storage_mb?.toLocaleString() || 0} MB
                        </div>
                        {totalEntitlements.breakdown && (
                          <div className="text-xs text-gray-500 mt-1">
                            Base: {totalEntitlements.breakdown.fromBasePlan?.storage_mb || 0} MB | 
                            Top-ups: {totalEntitlements.breakdown.fromTopups?.storage_mb || 0} MB
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Active Top-ups */}
                {companyTopups.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Package className="w-5 h-5 text-blue-600" />
                      <h3 className="text-xl font-bold text-gray-800">Active Top-ups</h3>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {companyTopups.filter(t => t.status === 'active').length}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {companyTopups
                        .filter(t => t.status === 'active')
                        .map((topup, index) => (
                          <div key={index} className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-semibold text-gray-800">{topup.name}</div>
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                                {topup.source === 'plan_upgrade' ? '🎁 BONUS' : 'PURCHASED'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {topup.remaining?.firebase_operations > 0 && (
                                <div className="text-gray-700">
                                  📊 {topup.remaining.firebase_operations.toLocaleString()} operations left
                                </div>
                              )}
                              {topup.remaining?.storage_mb > 0 && (
                                <div className="text-gray-700">
                                  💾 {topup.remaining.storage_mb} MB left
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-2">
                              Purchased: {new Date(topup.purchaseDate).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Current Usage Stats */}
                {usageData && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                        <h3 className="text-xl font-bold text-gray-800">Current Usage</h3>
                      </div>
            <button
                        onClick={() => loadUsageData(selectedCompany.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
        </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Firebase Operations */}
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold text-gray-800">Operations</span>
            </div>
                        <div className="text-2xl font-bold text-blue-700">
                          {usageData.operations.toLocaleString()}
                </div>
                        <div className="text-sm text-gray-600">
                          / {getCurrentLimits().firebase_operations.toLocaleString()}
                      </div>
                        <div className="mt-2 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getProgressColor(
                              getUsagePercentage(usageData.operations, getCurrentLimits().firebase_operations)
                            )}`}
                            style={{
                              width: `${Math.min(
                                getUsagePercentage(usageData.operations, getCurrentLimits().firebase_operations),
                                100
                              )}%`
                            }}
                          />
              </div>
          </div>

                      {/* Storage */}
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <HardDrive className="w-5 h-5 text-purple-600" />
                          <span className="font-semibold text-gray-800">Storage</span>
                </div>
                        <div className="text-2xl font-bold text-purple-700">
                          {usageData.storage} MB
              </div>
                        <div className="text-sm text-gray-600">
                          / {getCurrentLimits().storage_mb} MB
          </div>
                        <div className="mt-2 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getProgressColor(
                              getUsagePercentage(usageData.storage, getCurrentLimits().storage_mb)
                            )}`}
                            style={{
                              width: `${Math.min(
                                getUsagePercentage(usageData.storage, getCurrentLimits().storage_mb),
                                100
                              )}%`
                            }}
                          />
        </div>
          </div>

                    </div>
                  </div>
                )}

                {/* Plan Selection */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="w-5 h-5 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-800">Select Plan</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {Object.values(plans).map(plan => (
                      <div
                        key={plan.id}
                        onClick={() => handlePlanChange(plan.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedPlan === plan.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="font-bold text-lg text-gray-800">{plan.name}</div>
                        <div className="text-2xl font-bold text-blue-600 my-2">
                          {plan.currency}{plan.price}
                          <span className="text-sm text-gray-600">/{plan.billing}</span>
                                </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>📊 {(plan.limits?.firebase_operations || 0).toLocaleString()} operations</div>
                          <div>💾 {plan.limits?.storage_mb || 0} MB storage</div>
                            </div>
                      </div>
                    ))}
                  </div>
            </div>

                {/* Custom Entitlement */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                      <h3 className="text-xl font-bold text-gray-800">Custom Entitlement</h3>
                </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={entitlementMode}
                        onChange={(e) => setEntitlementMode(e.target.checked)}
                        className="w-5 h-5 text-blue-600"
                      />
                      <span className="text-gray-700 font-medium">Enable Custom Limits</span>
                    </label>
                  </div>

                  {entitlementMode && (
                    <div className="space-y-4">
                      <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
                        <p className="text-sm text-yellow-800">
                          ⚠️ Custom entitlement allows you to override plan limits for this company.
                </p>
              </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Firebase Operations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                            Firebase Operations
                </label>
                <input
                            type="number"
                            value={customLimits.firebase_operations}
                            onChange={(e) => handleLimitChange('firebase_operations', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter limit"
                />
              </div>

                        {/* Storage MB */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                            Storage (MB)
                </label>
                <input
                            type="number"
                            value={customLimits.storage_mb}
                            onChange={(e) => handleLimitChange('storage_mb', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter limit"
                />
              </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sales documents
                          </label>
                          <input
                            type="number"
                            value={customLimits.sales_documents ?? 0}
                            onChange={(e) => handleLimitChange('sales_documents', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter limit"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Parties
                          </label>
                          <input
                            type="number"
                            value={customLimits.parties ?? 0}
                            onChange={(e) => handleLimitChange('parties', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter limit"
                          />
                        </div>
                              </div>
                        </div>
                      )}
            </div>

                {/* Save Button */}
                <div className="bg-white rounded-xl shadow-lg p-6">
              <button
                    onClick={handleSaveSubscription}
                    disabled={loading || plansLoading || !!plansError || !plans[selectedPlan]}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-3 hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-6 h-6" />
                    {loading ? 'Saving...' : 'Save Subscription'}
              </button>
            </div>
          </>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Company Selected</h3>
                <p className="text-gray-600">Please select a company from the left panel to manage their subscription</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;

